import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, Paper, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Chip, IconButton, Tooltip, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Button, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Euro, Info, LocationOn, School, CalendarToday, People, DeleteOutline, Add } from '@mui/icons-material';
import ModalConfirm from '../../components/ModalConfirm';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useTeacher } from '../../contexts/TeacherContext';
import { useAuth } from '../../contexts/AuthContext';
import TeacherModalEnrollmentDetail from './TeacherModalEnrollmentDetail';

import ModalResult from '../../components/ModalResult';
import ModalPaymentTeacher from './ModalPaymentTeacher';
import CourseFiles from '../../components/CourseFiles';
import getConfig from '../../config/app-config';
import PaymentsGrid from './PaymentsGrid';

const paymentMethods = {
  monthly: 'Mensile',
  annual3: 'Annuale (3 rate)',
  annual1: 'Annuale (1 rata)',
};

const TeacherCourse = () => {
  const { id } = useParams();
  const config = getConfig();
  const { user } = useAuth();
  const { getCourseById, getEnrollmentsByCourseId, removeCourseDate, addCourseDate, setAttendance, removeAttendance } = useTeacher();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [error, setError] = useState(null);
  const [dates, setDates] = useState([]);
  const [openEnrollmentDetail, setOpenEnrollmentDetail] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [selectedPaymentEnrollment, setSelectedPaymentEnrollment] = useState(null);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  // const [attendanceLoading, setAttendanceLoading] = useState({});
  const [tableRef, setTableRef] = useState(null);
  const [openConfirmRemove, setOpenConfirmRemove] = useState(false);
  const [dateToRemove, setDateToRemove] = useState(null);
  const [openAddDate, setOpenAddDate] = useState(false);
  const [newDate, setNewDate] = useState('');
  // ModalResult per errori
  const [modalResultOpen, setModalResultOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    Promise.all([getCourseById(id), getEnrollmentsByCourseId(id)])
      .then(([courseRes, enrollmentsRes]) => {
        if (!isMounted) return;
        if (courseRes.success && enrollmentsRes.success) {
          setCourse(courseRes.data);
          setEnrollments(enrollmentsRes.data);
          // Aggiorna le date con quelle del corso
          if (courseRes.data && Array.isArray(courseRes.data.dates)) {
            // Ordina le date prima di mapparle
            const sortedDates = courseRes.data.dates.sort((a, b) => new Date(a) - new Date(b)).map((d, idx) => ({ id: d, date: d.slice(0, 10) }));
            setDates(sortedDates);
            // console.log('sortedDates', sortedDates);
          } else {
            setDates([]);
          }
        } else {
          setError('Errore nel caricamento dei dati');
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setError('Errore nel caricamento dei dati');
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id, getCourseById, getEnrollmentsByCourseId]);

  // Filtra gli enrollments per questo corso
  const activeEnrollments = enrollments.filter((enrollment) => enrollment.status !== 'suspended');

  const handleRemoveDate = async (date) => {
    setLoadingRemove(true);
    try {
      const result = await removeCourseDate(id, date);
      if (result.success) {
        // Ricarica i dati del corso per aggiornare le date
        const courseRes = await getCourseById(id);
        if (courseRes.success) {
          setCourse(courseRes.data);
          if (courseRes.data && Array.isArray(courseRes.data.dates)) {
            // Ordina le date prima di mapparle
            const sortedDates = courseRes.data.dates.sort((a, b) => new Date(a) - new Date(b)).map((d, idx) => ({ id: d, date: d.slice(0, 10) }));
            setDates(sortedDates);
          }
        }
      } else {
        setError('Errore nella rimozione della data');
      }
    } catch (err) {
      setError('Errore nella rimozione della data');
    } finally {
      setLoadingRemove(false);
      setOpenConfirmRemove(false);
      setDateToRemove(null);
    }
  };

  const handleAddDate = async (date) => {
    setLoadingAdd(true);
    try {
      const result = await addCourseDate(id, date);
      if (result.success) {
        // Ricarica i dati del corso per aggiornare le date
        const courseRes = await getCourseById(id);
        if (courseRes.success) {
          setCourse(courseRes.data);
          if (courseRes.data && Array.isArray(courseRes.data.dates)) {
            // Ordina le date prima di mapparle
            const sortedDates = courseRes.data.dates.sort((a, b) => new Date(a) - new Date(b)).map((d, idx) => ({ id: d, date: d.slice(0, 10) }));
            setDates(sortedDates);
          }
        }
        setNewDate('');
        setOpenAddDate(false);
      } else {
        setError("Errore nell'aggiunta della data");
      }
    } catch (err) {
      setError("Errore nell'aggiunta della data");
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleOpenPayment = (enrollment) => {
    setSelectedPaymentEnrollment(enrollment);
    setOpenPaymentModal(true);
  };

  // Funzione per aggiornare un enrollment specifico dopo il pagamento
  const handlePaymentUpdate = (updatedEnrollment) => {
    setEnrollments((prevEnrollments) => prevEnrollments.map((enrollment) => (enrollment._id === updatedEnrollment._id ? updatedEnrollment : enrollment)));
  };

  // Funzione per gestire l'attendance toggle
  const handleAttendanceToggle = async (enrollmentId, date, currentStatus) => {
    // Cicla tra i tre stati: present -> absent -> vuoto (rimozione)
    let newStatus;
    let shouldRemove = false;

    if (currentStatus === 'present') {
      newStatus = 'absent';
    } else if (currentStatus === 'absent') {
      // Terzo clic: rimuovi la presenza
      shouldRemove = true;
    } else {
      // Da vuoto a present
      newStatus = 'present';
    }

    // const loadingKey = `${enrollmentId}-${date}`;

    // setAttendanceLoading((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      let result;
      if (shouldRemove) {
        // Chiama l'endpoint di rimozione
        result = await removeAttendance(enrollmentId, date);
      } else {
        // Chiama l'endpoint di set/update
        result = await setAttendance(enrollmentId, date, newStatus);
      }

      if (result.success) {
        // Aggiorna lo stato locale degli enrollments
        setEnrollments((prevEnrollments) => prevEnrollments.map((enrollment) => (enrollment._id === enrollmentId ? { ...enrollment, attendances: result.data.attendances } : enrollment)));
      } else {
        setError('Errore nel salvataggio della presenza');
      }
    } catch (err) {
      setError('Errore nel salvataggio della presenza');
    } finally {
      // setAttendanceLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Funzione per ottenere lo status dell'attendance per una data specifica
  const getAttendanceStatus = (enrollment, date) => {
    if (!enrollment.attendances) return null;
    const attendance = enrollment.attendances.find((a) => a.date && a.date.slice(0, 10) === date);
    return attendance ? attendance.status : null;
  };

  // Funzione per aprire la modal di conferma rimozione
  const handleOpenRemoveConfirm = (date) => {
    setDateToRemove(date);
    setOpenConfirmRemove(true);
  };

  // Funzione per gestire l'aggiunta di una nuova data
  const handleAddNewDate = () => {
    if (newDate) {
      handleAddDate(newDate);
    }
  };

  // Funzione per trovare la data più vicina a oggi
  const findClosestDateIndex = useCallback(() => {
    if (!dates || dates.length === 0) return 0;

    const today = dayjs().startOf('day');
    let closestIndex = 0;
    let minDiff = Infinity;

    // Prima cerca una data futura (preferibile)
    let foundFuture = false;
    dates.forEach((dateObj, index) => {
      const date = dayjs(dateObj.date);
      const diff = date.diff(today, 'day');

      // Se è una data futura e non abbiamo ancora trovato una data futura
      if (diff >= 0 && !foundFuture) {
        closestIndex = index;
        minDiff = diff;
        foundFuture = true;
      }
      // Se è una data futura e più vicina di quella attuale
      else if (diff >= 0 && diff < minDiff) {
        closestIndex = index;
        minDiff = diff;
      }
    });

    // Se non abbiamo trovato date future, cerca la data passata più recente
    if (!foundFuture) {
      dates.forEach((dateObj, index) => {
        const date = dayjs(dateObj.date);
        const diff = Math.abs(date.diff(today, 'day'));

        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = index;
        }
      });
    }

    // console.log('Today:', today.format('YYYY-MM-DD'));
    // console.log('Closest date:', dayjs(dates[closestIndex]?.date).format('YYYY-MM-DD'), 'at index:', closestIndex);
    // console.log('All dates:', dates.map(d => dayjs(d.date).format('YYYY-MM-DD')));

    return closestIndex;
  }, [dates]);

  // Effetto per centrare la tabella sulla data più vicina
  useEffect(() => {
    if (tableRef && dates.length > 0) {
      const closestIndex = findClosestDateIndex();

      // console.log('Table ref found:', !!tableRef);
      // console.log('Table ref element:', tableRef);

      // Il ref è direttamente il TableContainer, non serve cercare
      const tableContainer = tableRef;

      // console.log('Table container found:', !!tableContainer);

      if (tableContainer) {
        // Calcola la larghezza approssimativa di una colonna (80px + padding)
        const columnWidth = 100;
        const scrollPosition = Math.max(0, closestIndex * columnWidth - tableContainer.clientWidth / 2 + columnWidth);

        // console.log('Scroll position calculated:', scrollPosition);
        // console.log('Container width:', tableContainer.clientWidth);

        // Aggiungi un piccolo delay per assicurarsi che il DOM sia completamente renderizzato
        setTimeout(() => {
          tableContainer.scrollTo({
            left: scrollPosition,
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  }, [dates, tableRef, findClosestDateIndex]);

  if (loading)
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='40vh'>
        <CircularProgress />
      </Box>
    );

  if (!course) return <Typography>Nessun corso trovato.</Typography>;

  const docente = course.teacher ? `${course.teacher.name} ${course.teacher.surname}` : 'Docente non assegnato';

  console.log('activeEnrollments', activeEnrollments);
  return (
    <Container maxWidth='xl' sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>{course.name}</Typography>
        <Typography variant='h6' color='text.secondary'>
          {docente}
        </Typography>
      </Box>

      {/* Info corso in un Paper separato */}
      <Paper
        sx={{
          p: 3,
          mb: 2,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '1px solid #e3e6f0',
          borderRadius: 2,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
          },
        }}
      >
        <Typography
          variant='h6'
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Info sx={{ fontSize: 24 }} />
          Info Corso
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={course.status === 'published' ? 'Pubblicato' : 'Bozza'} color={course.status === 'published' ? 'success' : 'default'} size='small' sx={{ fontWeight: 600 }} />
          </Box>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Sede: <span style={{ fontWeight: 400 }}>{course.venue}</span>
                </Typography>
              </Box>

              {course.minLevel && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <School sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant='body1' sx={{ fontWeight: 500 }}>
                    Anno di corso: <span style={{ fontWeight: 400 }}>{course.minLevel >= 6 ? 'Oltre il 5° anno' : `${course.minLevel}°`}</span>
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Posti:{' '}
                  <span style={{ fontWeight: 400 }}>
                    {activeEnrollments.length}/{course.availableSeats}
                  </span>
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant='body1' sx={{ fontWeight: 500 }}>
                Data inizio: <span style={{ fontWeight: 400 }}>{dayjs(course.startDate).locale('it').format('DD-MMM-YY')}</span>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Data fine: <span style={{ fontWeight: 400 }}>{dayjs(course.endDate).locale('it').format('DD-MMM-YY')}</span>
                </Typography>
              </Box>
            </Box>
            {course.weekdays && course.weekdays.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Giorni:{' '}
                  <span style={{ fontWeight: 400 }}>
                    {course.weekdays
                      .sort((a, b) => a - b)
                      .map((day) => {
                        const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
                        return dayNames[day];
                      })
                      .join(', ')}
                  </span>
                  {course.time && <span style={{ fontWeight: 400, marginLeft: '8px' }}>- {course.time}</span>}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Tabella Presenze */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant='h6' gutterBottom>
          Registro Presenze
        </Typography>

        {dates.length > 0 ? (
          <TableContainer
            ref={setTableRef}
            sx={{
              maxWidth: '100%',
              overflowX: 'auto',
              position: 'relative',
              '&::-webkit-scrollbar': {
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}
          >
            <Table
              size='small'
              sx={{
                minWidth: { xs: 'auto', sm: Math.max(600, dates.length * 100) },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      minWidth: { xs: 80, sm: 150 },
                      maxWidth: { xs: 120, sm: 'none' },
                      width: { xs: 100, sm: 'auto' },
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 2,
                      borderRight: '2px solid',
                      borderColor: 'divider',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: 'divider',
                      },
                    }}
                  >
                    Iscritto
                  </TableCell>
                  {dates.map((dateObj, index) => {
                    const isToday = dayjs(dateObj.date).isSame(dayjs(), 'day');
                    const isClosestDate = index === findClosestDateIndex();
                    const shouldHighlight = isToday || isClosestDate;

                    return (
                      <TableCell
                        key={dateObj.id}
                        align='center'
                        sx={{
                          fontWeight: 'bold',
                          minWidth: 80,
                          backgroundColor: shouldHighlight ? 'primary.light' : 'inherit',
                          color: shouldHighlight ? 'primary.contrastText' : 'inherit',
                          borderRadius: shouldHighlight ? 1 : 0,
                          position: 'relative',
                          '&::after': {},
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={`Rimuovi ${dayjs(dateObj.date).locale('it').format('DD/MM/YYYY')}`}>
                            <span>
                              <IconButton
                                size='small'
                                color='error'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenRemoveConfirm(dateObj.date);
                                }}
                                disabled={loadingRemove}
                                sx={{
                                  padding: 0.5,
                                  minWidth: 'auto',
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                  },
                                }}
                              >
                                <DeleteOutline fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            {dayjs(dateObj.date).locale('it').format('DD/MM')}
                          </Typography>
                          <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {dayjs(dateObj.date).locale('it').format('YYYY')}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  })}
                  {/* {config.SHOW_PAYMENT_DETAILS_TEACHER_COURSE && (
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Azioni</TableCell>
                    )} */}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeEnrollments.length > 0 ? (
                  activeEnrollments.map((enrollment) => {
                    const user = enrollment.user || enrollment.userId;
                    const userName = typeof user === 'object' ? `${user.name} ${user.surname}` : user;

                    return (
                      <TableRow key={enrollment._id} hover>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            minWidth: { xs: 80, sm: 150 },
                            maxWidth: { xs: 120, sm: 'none' },
                            width: { xs: 100, sm: 'auto' },
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'background.paper',
                            zIndex: 1,
                            borderRight: '2px solid',
                            borderColor: 'divider',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bottom: 0,
                              width: '2px',
                              backgroundColor: 'divider',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              overflow: 'hidden',
                              fontSize: { xs: '0.8rem', sm: 'inherit' },
                              // Su mobile: permette wrapping su due righe
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              textOverflow: { xs: 'clip', sm: 'ellipsis' },
                              display: { xs: '-webkit-box', sm: 'block' },
                              WebkitLineClamp: { xs: 2, sm: 'none' },
                              WebkitBoxOrient: { xs: 'vertical', sm: 'horizontal' },
                              lineHeight: { xs: 1.2, sm: 'inherit' },
                            }}
                          >
                            {userName}
                          </Box>
                        </TableCell>
                        {dates.map((dateObj) => {
                          const attendanceStatus = getAttendanceStatus(enrollment, dateObj.date);
                          // const loadingKey = `${enrollment._id}-${dateObj.date}`;
                          // const isLoading = attendanceLoading[loadingKey];
                          // const isLoading = false

                          return (
                            <TableCell key={dateObj.id} align='center'>
                              <Checkbox
                                checked={attendanceStatus === 'present'}
                                indeterminate={attendanceStatus === 'absent'}
                                onChange={() => handleAttendanceToggle(enrollment._id, dateObj.date, attendanceStatus)}
                                // disabled={isLoading}
                                color='primary'
                                sx={{
                                  '&.Mui-checked': {
                                    color: 'success.main',
                                  },
                                  '&.MuiCheckbox-indeterminate': {
                                    color: 'warning.main',
                                  },
                                  // Stile per stato vuoto (null/undefined)
                                  ...(attendanceStatus === null || attendanceStatus === undefined
                                    ? {
                                        color: 'text.disabled',
                                        '&:hover': {
                                          color: 'primary.main',
                                        },
                                      }
                                    : {}),
                                }}
                              />
                              {/* {isLoading && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )} */}
                            </TableCell>
                          );
                        })}
                        {/* {config.SHOW_PAYMENT_DETAILS_TEACHER_COURSE && (
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title='Visualizza dettagli iscrizione'>
                                <IconButton
                                  size='small'
                                  color='primary'
                                  onClick={() => {
                                    setSelectedEnrollment(enrollment);
                                    setOpenEnrollmentDetail(true);
                                  }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Registra pagamento'>
                                <IconButton 
                                  size='small' 
                                  color='success' 
                                  onClick={() => handleOpenPayment(enrollment)} 
                                  disabled={enrollment.paymentStatusInfo?.isEnrollmentPaid}
                                >
                                  <Euro />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )} */}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={dates.length + 1}
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      }}
                    >
                      Nessun iscritto a questo corso
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant='body2' color='text.secondary'>
              Nessuna data di lezione definita
            </Typography>
          </Box>
        )}

        {/* Pulsante Aggiungi Data */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant='contained' startIcon={<Add />} onClick={() => setOpenAddDate(true)} disabled={loadingAdd} sx={{ minWidth: 150 }}>
            Aggiungi Data
          </Button>
          {loadingAdd && <CircularProgress size={20} />}
        </Box>
      </Paper>

      {/* Materiali del Corso */}
      {config.WITH_COURSE_MATERIALS && <CourseFiles courseId={id} isTeacher={user?.roles?.includes('teacher')} isCourseTeacher={course?.teacherId === user?._id} isAdmin={user?.roles?.includes('admin')} />}

      {/* Griglia Pagamenti */}
      {config.SHOW_PAYMENT_DETAILS_TEACHER_COURSE && <PaymentsGrid enrollments={activeEnrollments} course={course} />}

      {/* DataGrid Gestione Pagamenti */}
      {config.SHOW_PAYMENT_DETAILS_TEACHER_COURSE && (
        // { (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant='h6' gutterBottom>
            Gestione Pagamenti
          </Typography>
          <DataGrid
            rows={activeEnrollments}
            columns={[
              {
                field: 'fullName',
                headerName: 'Iscritto',
                flex: 1.2,
                minWidth: 150,
                resizable: true,
                renderCell: (params) => {
                  const name = params.row.user?.name || (typeof params.row.userId === 'object' ? params.row.userId?.name : '');
                  const surname = params.row.user?.surname || (typeof params.row.userId === 'object' ? params.row.userId?.surname : '');
                  return `${name} ${surname}`.trim();
                },
              },
              {
                field: 'paymentMethod',
                headerName: 'Pagamento',
                flex: 1,
                minWidth: 120,
                resizable: true,
                renderCell: (params) => paymentMethods[params.value] || params.value,
              },
              {
                field: 'amount',
                headerName: 'Importo',
                flex: 0.7,
                minWidth: 90,
                resizable: true,
                renderCell: (params) => `€${params.value}`,
              },
              {
                field: 'paymentStatus',
                headerName: 'Stato',
                flex: 1,
                minWidth: 120,
                resizable: true,
                renderCell: (params) => <Chip label={params.row.paymentStatusInfo?.isEnrollmentPaid ? 'Pagato' : 'Da Pagare'} color={params.row.paymentStatusInfo?.isEnrollmentPaid ? 'success' : 'warning'} size='small' />,
              },
              {
                field: 'actions',
                headerName: 'Azioni',
                flex: 1,
                minWidth: 120,
                resizable: true,
                renderCell: (params) => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title='Visualizza dettagli iscrizione'>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={() => {
                          setSelectedEnrollment(params.row);
                          setOpenEnrollmentDetail(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Registra pagamento'>
                      <span>
                        <IconButton size='small' color='success' onClick={() => handleOpenPayment(params.row)} disabled={params.row.paymentStatusInfo?.isEnrollmentPaid}>
                          <Euro />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ),
              },
            ]}
            getRowId={(row) => row._id || row.userId || Math.random()}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
            sx={{ width: '100%' }}
            hideFooterPagination={activeEnrollments.length <= 10}
          />
        </Paper>
      )}

      {/* Paper sotto la tabella per storico date lezione e modali */}
      <Paper sx={{ p: 3, mt: 2 }}>
        {/* <TeacherCourseDates dates={dates} onRemoveDate={handleRemoveDate} onAddDate={handleAddDate} loadingRemove={loadingRemove} loadingAdd={loadingAdd} enrollments={courseEnrollments} /> */}

        {/* Modale dettaglio iscrizione */}
        <TeacherModalEnrollmentDetail open={openEnrollmentDetail} onClose={() => setOpenEnrollmentDetail(false)} enrollment={selectedEnrollment} />

        {/* Modale registrazione pagamento */}
        <ModalPaymentTeacher open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} enrollment={selectedPaymentEnrollment} onPaymentUpdate={handlePaymentUpdate} />

        <ModalResult
          open={modalResultOpen}
          onClose={() => {
            setModalResultOpen(false);
            setError(null);
          }}
          message={error}
          severity='error'
          title='Errore'
        />
      </Paper>

      {/* Modal Conferma Rimozione Data */}
      <ModalConfirm
        open={openConfirmRemove}
        title='Rimuovi Data'
        message={`Confermi di voler rimuovere la data ${dateToRemove ? dayjs(dateToRemove).locale('it').format('DD/MM/YYYY') : ''}?`}
        onConfirm={() => handleRemoveDate(dateToRemove)}
        onCancel={() => {
          setOpenConfirmRemove(false);
          setDateToRemove(null);
        }}
      />

      {/* Modal Aggiungi Data */}
      <Dialog
        open={openAddDate}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setOpenAddDate(false);
        }}
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Aggiungi Data</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField label='Data' type='date' value={newDate} onChange={(e) => setNewDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDate(false)}>Annulla</Button>
          <Button onClick={handleAddNewDate} variant='contained' disabled={!newDate || loadingAdd}>
            Aggiungi
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeacherCourse;
